import { useEffect, useState } from "react";
import { getIdToken, isCoach, currentUsername } from "./auth";

export interface SignedInState {
  /** Null while the local session check is still resolving -- it is storage, not a network call. */
  signedIn: boolean | null;
  /** Where this person's app actually starts. */
  destination: string;
  username: string | null;
}

/**
 * Whether there is a live session, and where it leads.
 *
 * Two screens need this and neither had it. The root page rendered the signed-out marketing page with a
 * "Log in to your account" button to somebody who was already logged in — the root being what people
 * type, bookmark, and land on from "open in new tab" (UX review F18). And `/signin` silently threw an
 * already-signed-in visitor into the app, which leaves a shared family tablet with no route to the
 * sign-in form at all short of hunting for Sign out (F91).
 *
 * The token is already in local storage, so this costs no request.
 */
export function useSignedIn(): SignedInState {
  const [state, setState] = useState<SignedInState>({ signedIn: null, destination: "/family", username: null });

  useEffect(() => {
    let cancelled = false;

    /**
     * Reads as "signed out" if anything at all goes wrong, deliberately.
     *
     * The root page is public and renders before the runtime config has necessarily loaded, and the
     * Cognito pool this reaches through needs that config -- so asking the question at all can throw on
     * exactly the page that most needs to survive it. Treating any failure as "no session" is both true
     * enough (there is certainly no usable session) and the safe direction: the worst case is showing a
     * signed-in visitor the ordinary "Log in" button, which is where they were before this existed.
     */
    function signedOut() {
      if (!cancelled) setState({ signedIn: false, destination: "/family", username: null });
    }

    try {
      getIdToken().then((token) => {
        if (cancelled) return;
        if (!token) {
          signedOut();
          return;
        }
        isCoach().then((coach) => {
          if (cancelled) return;
          setState({
            signedIn: true,
            destination: coach ? "/dashboard" : "/family",
            username: safeUsername(),
          });
        }, signedOut);
      }, signedOut);
    } catch {
      signedOut();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Same reasoning as the guard above: a name is decoration here, never worth throwing for. */
function safeUsername(): string | null {
  try {
    return currentUsername();
  } catch {
    return null;
  }
}
